import React, { useEffect, useRef, useState } from 'react';
import { ConfirmDialog, Notice } from './admin-feedback';
import { isRecord, type AdminApiClient } from './api-client';
import { canonicalBusinessFieldName } from '@travel/contracts';
import {
  BUSINESS_PERMISSION_GROUPS,
  FIELD_CHOICES,
  permissionActionName,
  presentPermission,
  updateBusinessGroup,
  updateBusinessPermission,
  type BusinessSecurityWorkspace as WorkspaceData,
  type BusinessScopeGroup,
  type FunctionRule,
  type FieldRule,
  type SecurityPermission,
} from './business-security-model';
import './permission-scope-editor.css';

type Props = {
  api?: AdminApiClient;
  data: WorkspaceData;
  permissions: FunctionRule[];
  groups: BusinessScopeGroup[] | null;
  disabled: boolean;
  onChange: (
    permissions: FunctionRule[],
    groups: BusinessScopeGroup[] | null,
    completeCommonReadFields?: boolean,
  ) => void;
};

type ScopeOrder = WorkspaceData['orders'][number];
type OrderLookup = { orders: ScopeOrder[]; busy: boolean; hasMore: boolean; error: string };
async function lookupWithin(api: AdminApiClient, path: string, controller: AbortController) {
  let cancel!: () => void;
  const cancelled = new Promise<never>((_, reject) => {
    cancel = () => reject(new Error('订单查询已取消。'));
    controller.signal.addEventListener('abort', cancel, { once: true });
  });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 15_000);
  try {
    return await Promise.race([
      api.request<unknown>(path, { signal: controller.signal, cache: 'no-store' }),
      cancelled,
    ]);
  } catch (error) {
    if (timedOut) throw new Error('订单查询超时，请重试。', { cause: error });
    throw error;
  } finally {
    clearTimeout(timer);
    controller.signal.removeEventListener('abort', cancel);
  }
}

export function PermissionFieldSettings({
  rules,
  catalog,
  disabled,
  onChange,
  onComplete,
}: {
  rules: readonly FieldRule[];
  catalog: readonly SecurityPermission[];
  disabled: boolean;
  onChange: (rules: FieldRule[]) => void;
  onComplete?: () => void;
}) {
  return (
    <details className="permission-editor-section permission-field-settings">
      <summary>常用信息显示与脱敏（高级） · {rules.length} 项设置</summary>
      <p>
        勾选查看功能时，会补齐该功能的常用联系和交易金额字段。证件、成本和支付密钥不会自动开放；原有脱敏或禁止设置保持生效。
      </p>
      {onComplete && (
        <button type="button" disabled={disabled} onClick={onComplete}>
          补齐常用显示字段
        </button>
      )}
      <div className="permission-field-list">
        {rules.map((rule, index) => {
          const label =
            FIELD_CHOICES.find((field) => field.name === canonicalBusinessFieldName(rule.fieldName))
              ?.label ?? '原有业务信息';
          const permission = catalog.find((item) => item.id === rule.permissionId);
          return (
            <label key={`${rule.permissionId}:${rule.fieldName}:${index}`}>
              <span>
                <strong>{label}</strong>
                <small>{permission ? presentPermission(permission).title : '原有业务授权'}</small>
              </span>
              <select
                aria-label={`${label} · ${permission ? presentPermission(permission).title : '原有业务授权'}显示方式`}
                value={rule.access}
                disabled={disabled}
                onChange={(event) =>
                  onChange(
                    rules.map((current, position) =>
                      position === index
                        ? { ...current, access: event.target.value as FieldRule['access'] }
                        : { ...current },
                    ),
                  )
                }
              >
                <option value="ALLOW" disabled={rule.classification === 'L3'}>
                  可读原文
                </option>
                <option value="MASK">脱敏显示</option>
                <option value="DENY">隐藏</option>
              </select>
            </label>
          );
        })}
      </div>
      {!rules.length && <p>暂无单独显示设置。选择查看功能时会补齐所需的常用字段。</p>}
    </details>
  );
}
function readOrderLookup(value: unknown): { orders: ScopeOrder[]; hasMore: boolean } {
  if (!isRecord(value) || !Array.isArray(value.orders) || typeof value.hasMore !== 'boolean')
    throw new Error('订单查询结果未完整读取，请重试。');
  return {
    hasMore: value.hasMore,
    orders: value.orders.map((order) => {
      if (
        !isRecord(order) ||
        ['id', 'orderNo', 'appId', 'cityId', 'productId'].some(
          (key) => typeof order[key] !== 'string',
        )
      )
        throw new Error('订单查询结果未完整读取，请重试。');
      return {
        id: order.id as string,
        orderNo: order.orderNo as string,
        appId: order.appId as string,
        cityId: order.cityId as string,
        productId: order.productId as string,
        organizationId: typeof order.organizationId === 'string' ? order.organizationId : null,
      };
    }),
  };
}

const copyGroups = (groups: readonly BusinessScopeGroup[]) =>
  groups.map((group) => ({
    ...group,
    cityIds: group.cityIds?.slice() ?? null,
    ...(group.productIds === undefined ? {} : { productIds: group.productIds?.slice() ?? null }),
    ...(group.orderIds === undefined ? {} : { orderIds: group.orderIds?.slice() ?? null }),
  }));
const toggleId = (ids: readonly string[], id: string, checked: boolean) =>
  checked ? [...new Set([...ids, id])] : ids.filter((item) => item !== id);
const ruleEffect = (rules: readonly FunctionRule[], id: string) =>
  rules.find((rule) => rule.permissionId === id)?.effect;

/** Edits a complete draft; the parent saves function and data scope changes together. */
export function PermissionScopeEditor({
  api,
  data,
  permissions,
  groups,
  disabled,
  onChange,
}: Props) {
  const hasAdvertisingAccess = data.permissions.some(
    (permission) =>
      ['ad_landing.view', 'ad_landing.manage', 'ad_landing.publish'].includes(permission.code) &&
      permissions.some((rule) => rule.permissionId === permission.id && rule.effect === 'ALLOW'),
  );
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState('');
  const latestDraft = useRef({ permissions, groups, disabled, catalog: data.permissions });
  latestDraft.current = { permissions, groups, disabled, catalog: data.permissions };
  const [emptyOrderModes, setEmptyOrderModes] = useState<Record<string, 'specific' | 'none'>>({});
  const [scopeSearch, setScopeSearch] = useState<
    Record<string, { product?: string; order?: string }>
  >({});
  const [orderLookups, setOrderLookups] = useState<Record<string, OrderLookup>>({});
  const lookupOperations = useRef(new Map<string, { controller: AbortController; id: number }>());
  const lookupSequence = useRef(0);
  const clientGeneration = useRef(0);
  useEffect(() => {
    const operations = lookupOperations.current;
    clientGeneration.current += 1;
    for (const { controller } of operations.values()) controller.abort();
    operations.clear();
    setOrderLookups({});
    setConfirmation(undefined);
    return () => {
      clientGeneration.current += 1;
      for (const { controller } of operations.values()) controller.abort();
      operations.clear();
    };
  }, [api]);
  async function searchOrders(appId: string, query: string) {
    if (!api) return;
    const text = query.trim();
    if (!text || text.length > 64) {
      setOrderLookups((current) => ({
        ...current,
        [appId]: {
          orders: current[appId]?.orders ?? [],
          busy: false,
          hasMore: false,
          error: !text ? '请输入订单号后查找。' : '订单号查询请限制在 64 个字符以内。',
        },
      }));
      return;
    }
    lookupOperations.current.get(appId)?.controller.abort();
    const controller = new AbortController(),
      id = ++lookupSequence.current,
      generation = clientGeneration.current;
    lookupOperations.current.set(appId, { controller, id });
    setOrderLookups((current) => ({
      ...current,
      [appId]: { orders: current[appId]?.orders ?? [], busy: true, hasMore: false, error: '' },
    }));
    const currentRequest = () =>
      generation === clientGeneration.current && lookupOperations.current.get(appId)?.id === id;
    try {
      const result = readOrderLookup(
        await lookupWithin(
          api,
          `/api/admin/m8/security/workspace/orders?appId=${encodeURIComponent(appId)}&query=${encodeURIComponent(text)}`,
          controller,
        ),
      );
      if (!currentRequest()) return;
      setOrderLookups((current) => ({
        ...current,
        [appId]: { ...result, busy: false, error: '' },
      }));
    } catch (error) {
      if (!currentRequest()) return;
      setOrderLookups((current) => ({
        ...current,
        [appId]: {
          orders: current[appId]?.orders ?? [],
          busy: false,
          hasMore: false,
          error:
            error instanceof Error && /[\u3400-\u9fff]/u.test(error.message)
              ? error.message
              : '订单查询失败，请重试。',
        },
      }));
    } finally {
      if (currentRequest()) lookupOperations.current.delete(appId);
    }
  }
  const [confirmation, setConfirmation] = useState<{
    title: string;
    description: string;
    action: () => void;
  }>();
  const catalog = data.permissions;
  function confirmDraft(input: { title: string; description: string; action: () => void }) {
    const snapshot = JSON.stringify({ permissions, groups, catalog });
    setConfirmation({
      ...input,
      action: () => {
        const latest = latestDraft.current;
        if (
          latest.disabled ||
          snapshot !==
            JSON.stringify({
              permissions: latest.permissions,
              groups: latest.groups,
              catalog: latest.catalog,
            })
        ) {
          setFeedback('管理范围或功能配置已发生变化，请重新确认授权。');
          setConfirmation(undefined);
          return;
        }
        input.action();
      },
    });
  }
  const filtered = catalog.filter((permission, index) =>
    presentPermission(permission, index).title.includes(search.trim()),
  );
  function editPermissions(update: () => FunctionRule[], completeCommonReadFields = false) {
    if (disabled) return;
    try {
      if (completeCommonReadFields) onChange(update(), groups, true);
      else onChange(update(), groups);
      setFeedback('');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : '请先检查对应的查看权限。');
    }
  }
  function editGroup(appId: string, update: (group: BusinessScopeGroup) => BusinessScopeGroup) {
    if (disabled || groups === null) return;
    onChange(
      permissions,
      copyGroups(groups).map((group) => (group.appId === appId ? update(group) : group)),
    );
  }
  const appNames = new Set(data.apps.map((app) => app.id));
  return (
    <div className="permission-scope-editor">
      <section
        className="permission-editor-section"
        aria-label="功能权限"
        id="permission-functions"
      >
        <div className="permission-editor-heading">
          <div>
            <h2>功能权限</h2>
            <p>先选择能做什么，再设置下方的管理范围。维护操作会同时选中所需的查看权限。</p>
          </div>
          <label>
            <span className="sr-only">查找功能权限</span>
            <input
              type="search"
              aria-label="查找功能权限"
              placeholder="查找功能，例如订单、退款"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
        {feedback && <Notice tone="warning">{feedback}</Notice>}
        <div className="permission-read-preset">
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              const choices = catalog.filter(
                (permission) =>
                  ['order.view', 'refund.view', 'payment.view'].includes(permission.code) &&
                  permission.status === 'ACTIVE' &&
                  !permission.highRisk &&
                  ruleEffect(permissions, permission.id) !== 'DENY',
              );
              editPermissions(() => updateBusinessGroup(permissions, catalog, choices, true), true);
            }}
          >
            只读查看订单、退款与流水
          </button>
          <span>只填入查看功能；退款执行、资金操作及原有明确禁止均保持原设置。</span>
        </div>
        <div className="permission-module-grid">
          {BUSINESS_PERMISSION_GROUPS.map((module) => {
            const choices = filtered.filter(
              (permission, index) => presentPermission(permission, index).groupId === module.id,
            );
            if (!choices.length) return null;
            const ordinary = choices.filter(
              (permission) =>
                permission.status === 'ACTIVE' &&
                !permission.highRisk &&
                presentPermission(permission).action !== 'sensitive' &&
                ruleEffect(permissions, permission.id) !== 'DENY',
            );
            const selected = choices.filter(
              (permission) =>
                permission.status === 'ACTIVE' &&
                ruleEffect(permissions, permission.id) === 'ALLOW',
            ).length;
            return (
              <details
                className="permission-module"
                key={module.id}
                open={Boolean(search.trim()) || selected > 0 || module.id === 'orders'}
              >
                <summary>
                  <strong>{module.name}</strong>
                  <span>已选 {selected} 项</span>
                </summary>
                {ordinary.length > 0 && (
                  <label className="permission-select-all">
                    <input
                      type="checkbox"
                      aria-label={`${module.name}常规操作全选`}
                      checked={ordinary.every(
                        (permission) => ruleEffect(permissions, permission.id) === 'ALLOW',
                      )}
                      disabled={disabled}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        editPermissions(() =>
                          updateBusinessGroup(permissions, catalog, ordinary, checked),
                        );
                      }}
                    />
                    {search.trim() ? '全选当前搜索结果中的常规操作' : '常规操作全选'}
                  </label>
                )}
                <div className="permission-action-list">
                  {choices.map((permission, index) => {
                    const presentation = presentPermission(permission, index);
                    const effect = ruleEffect(permissions, permission.id);
                    const important = permission.highRisk || presentation.action === 'sensitive';
                    return (
                      <label key={permission.id} className="permission-action">
                        <input
                          type="checkbox"
                          aria-label={presentation.title}
                          checked={effect === 'ALLOW'}
                          disabled={disabled || permission.status !== 'ACTIVE'}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            const apply = () =>
                              editPermissions(() =>
                                updateBusinessPermission(
                                  permissions,
                                  catalog,
                                  permission.id,
                                  checked,
                                ),
                              );
                            if (checked && (important || effect === 'DENY'))
                              confirmDraft({
                                title: '确认授权此操作？',
                                description: `允许“${presentation.title}”，实际执行仍受部门和管理范围限制。${effect === 'DENY' ? '这会解除该操作原有的明确禁止。' : ''}`,
                                action: apply,
                              });
                            else apply();
                          }}
                        />
                        <span>
                          {presentation.title}
                          <small>
                            {permissionActionName(presentation.action)}
                            {effect === 'DENY' ? ' · 已明确禁止' : ''}
                            {permission.status !== 'ACTIVE' ? ' · 已停用' : ''}
                          </small>
                        </span>
                        {important && <b className="permission-important">重要</b>}
                      </label>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
        {!filtered.length && <p className="permission-empty">没有匹配的功能，换个关键词再试。</p>}
      </section>

      <section
        className="permission-editor-section"
        aria-label="管理范围"
        id="permission-data-scope"
      >
        <div className="permission-editor-heading">
          <div>
            <h2>管理范围</h2>
            <p>
              {hasAdvertisingAccess && '广告页仅需选择小程序，无需配置城市。'}
              每个小程序分别选择城市、线路和订单；部门订单仍受用户所属组织限制。尚未分配部门的中央订单，可在明确选择城市后单独包含。选择范围不会改变订单归属。
            </p>
          </div>
        </div>
        {groups === null ? (
          <div className="permission-legacy-scope">
            <strong>沿用已有管理范围</strong>
            <p>原有授权继续生效。重新设置将从空范围开始，未选择的业务数据不会开放。</p>
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                confirmDraft({
                  title: '重新设置管理范围？',
                  description:
                    '从空范围开始选择小程序、城市、线路和订单。保存后未选择的业务数据将不可管理；原有功能和明确禁止保持不变。',
                  action: () => {
                    if (!disabled) onChange(permissions, []);
                  },
                })
              }
            >
              重新设置管理范围
            </button>
          </div>
        ) : (
          <>
            {groups.length === 0 && (
              <Notice>
                {hasAdvertisingAccess
                  ? '尚未选择管理范围。勾选小程序即可管理其广告页；其他业务仍按城市、线路和订单范围授权。'
                  : '尚未选择管理范围。先勾选小程序，再选择可管理的城市。'}
              </Notice>
            )}
            <div className="permission-app-list">
              {data.apps.map((app) => {
                const scope = groups.find((group) => group.appId === app.id);
                if (app.status !== 'ACTIVE' && !scope) return null;
                const cities = data.cities.filter(
                  (city) =>
                    (city.status === 'ACTIVE' || scope?.cityIds?.includes(city.id)) &&
                    (app.cityIds === null || app.cityIds.includes(city.id)),
                );
                const allowedCities = (cityId: string) =>
                  Boolean(scope && (scope.cityIds === null || scope.cityIds.includes(cityId)));
                const products = data.products.filter(
                  (product) =>
                    product.status === 'ACTIVE' &&
                    product.appIds.includes(app.id) &&
                    allowedCities(product.cityId),
                );
                const productNames = new Map(
                  data.products.map((product) => [product.id, product.name]),
                );
                const knownOrders = [
                  ...new Map(
                    [...data.orders, ...(orderLookups[app.id]?.orders ?? [])].map((order) => [
                      order.id,
                      order,
                    ]),
                  ).values(),
                ];
                const orders = knownOrders.filter(
                  (order) =>
                    order.appId === app.id &&
                    allowedCities(order.cityId) &&
                    (!scope?.productIds || scope.productIds.includes(order.productId)),
                );
                const orderNames = new Map(knownOrders.map((order) => [order.id, order.orderNo]));
                const productSearch = scopeSearch[app.id]?.product ?? '';
                const orderSearch = scopeSearch[app.id]?.order ?? '';
                const displayedProducts = products.filter((product) =>
                  product.name.toLowerCase().includes(productSearch.trim().toLowerCase()),
                );
                const displayedOrders = orders.filter((order) =>
                  `${order.orderNo} ${productNames.get(order.productId) ?? ''}`
                    .toLowerCase()
                    .includes(orderSearch.trim().toLowerCase()),
                );
                const hiddenProducts = (scope?.productIds ?? []).filter(
                  (id) => !products.some((product) => product.id === id),
                );
                const hiddenCities = (scope?.cityIds ?? []).filter(
                  (id) => !cities.some((city) => city.id === id),
                );
                const hiddenOrders = (scope?.orderIds ?? []).filter(
                  (id) => !orders.some((order) => order.id === id),
                );
                const scopedDisabled = disabled || !scope || app.status !== 'ACTIVE';
                const productMode = scope?.productIds == null ? 'all' : 'specific';
                const orderMode =
                  scope?.orderIds == null
                    ? 'all'
                    : scope.orderIds.length === 0
                      ? (emptyOrderModes[app.id] ?? 'none')
                      : 'specific';
                return (
                  <article className="permission-app-card" key={app.id}>
                    <header>
                      <label>
                        <input
                          type="checkbox"
                          aria-label={`管理${app.name}`}
                          checked={Boolean(scope)}
                          disabled={disabled || (app.status !== 'ACTIVE' && !scope)}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            if (disabled) return;
                            const next = copyGroups(groups).filter(
                              (group) => group.appId !== app.id,
                            );
                            onChange(
                              permissions,
                              checked
                                ? [
                                    ...next,
                                    {
                                      appId: app.id,
                                      cityIds: [],
                                      productIds: null,
                                      orderIds: null,
                                    },
                                  ]
                                : next,
                            );
                          }}
                        />
                        <strong>{app.name}</strong>
                      </label>
                      <span>
                        {!scope ? '未分配' : app.status !== 'ACTIVE' ? '小程序已停用' : '已选择'}
                      </span>
                    </header>
                    {scope && (
                      <div className="permission-app-content">
                        <div className="permission-scope-row">
                          <h3>城市</h3>
                          <label className="permission-select-all">
                            <input
                              type="checkbox"
                              aria-label={`${app.name}全部城市`}
                              checked={scope.cityIds === null}
                              disabled={scopedDisabled}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                editGroup(app.id, (group) => ({
                                  ...group,
                                  cityIds: checked ? null : [],
                                  ...(group.includeCentralOrders === undefined
                                    ? {}
                                    : { includeCentralOrders: false }),
                                }));
                              }}
                            />
                            全部已开放城市（含以后开放的城市）
                          </label>
                          <div className="permission-scope-choices">
                            {cities.map((city) => (
                              <label key={city.id}>
                                <input
                                  type="checkbox"
                                  aria-label={`${app.name} / ${city.name}`}
                                  checked={
                                    scope.cityIds === null || scope.cityIds.includes(city.id)
                                  }
                                  disabled={scopedDisabled}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    editGroup(app.id, (group) => {
                                      const cityIds = toggleId(
                                        group.cityIds ?? cities.map((item) => item.id),
                                        city.id,
                                        checked,
                                      );
                                      return {
                                        ...group,
                                        cityIds,
                                        ...(group.includeCentralOrders && cityIds.length === 0
                                          ? { includeCentralOrders: false }
                                          : {}),
                                      };
                                    });
                                  }}
                                />
                                {city.name}
                              </label>
                            ))}
                          </div>
                          {!cities.length && <p>暂无已开放城市。</p>}
                          {hiddenCities.length > 0 && (
                            <p className="permission-scope-note">
                              另有 {hiddenCities.length}{' '}
                              个原有城市未在当前可选范围中展示，其配置保留。
                            </p>
                          )}
                          {scope.cityIds?.length === 0 && (
                            <p className="permission-scope-note">
                              {hasAdvertisingAccess
                                ? '已选择此小程序的广告页范围；未选择城市，线路和订单仍不可管理。'
                                : '还未选择城市，此小程序下的线路和订单暂不可管理。'}
                            </p>
                          )}
                          {scope.includeCentralOrders && (
                            <p className="permission-scope-note">
                              可处理以上范围内尚未分配部门的订单（含新增订单）。
                            </p>
                          )}
                        </div>
                        <div className="permission-scope-row">
                          <label className="permission-scope-mode">
                            <strong>线路</strong>
                            <select
                              aria-label={`${app.name}线路范围`}
                              value={productMode}
                              disabled={scopedDisabled}
                              onChange={(event) =>
                                editGroup(app.id, (group) => ({
                                  ...group,
                                  productIds: event.target.value === 'all' ? null : [],
                                }))
                              }
                            >
                              <option value="all">所选城市的全部线路</option>
                              <option value="specific">仅选定线路</option>
                            </select>
                          </label>
                          {productMode === 'specific' && (
                            <>
                              <input
                                type="search"
                                className="permission-scope-search"
                                aria-label={`${app.name}查找线路`}
                                placeholder="输入线路名称查找"
                                value={productSearch}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setScopeSearch((current) => ({
                                    ...current,
                                    [app.id]: { ...current[app.id], product: value },
                                  }));
                                }}
                              />
                              <div className="permission-scope-choices permission-record-choices">
                                {displayedProducts.map((product) => (
                                  <label key={product.id}>
                                    <input
                                      type="checkbox"
                                      aria-label={`${app.name}线路：${product.name}`}
                                      checked={scope.productIds?.includes(product.id) ?? false}
                                      disabled={scopedDisabled}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        editGroup(app.id, (group) => ({
                                          ...group,
                                          productIds: toggleId(
                                            group.productIds ?? [],
                                            product.id,
                                            checked,
                                          ),
                                        }));
                                      }}
                                    />
                                    {product.name}
                                  </label>
                                ))}
                                {!products.length && <p>当前城市范围内暂无可选择线路。</p>}
                                {products.length > 0 && displayedProducts.length === 0 && (
                                  <p>没有匹配的线路，请调整关键词。</p>
                                )}
                                {hiddenProducts.map((id) => (
                                  <label className="permission-retained-choice" key={id}>
                                    <input
                                      type="checkbox"
                                      checked
                                      disabled={scopedDisabled}
                                      aria-label={`保留线路：${productNames.get(id) ?? '原有线路'}`}
                                      onChange={() =>
                                        editGroup(app.id, (group) => ({
                                          ...group,
                                          productIds:
                                            group.productIds?.filter((item) => item !== id) ?? [],
                                        }))
                                      }
                                    />
                                    {productNames.get(id) ?? '原有线路'}
                                    （不在当前可选范围，配置保留）
                                  </label>
                                ))}
                                {scope.productIds?.length === 0 && (
                                  <p className="permission-scope-note">
                                    未选择线路，此小程序下的线路和关联订单不可管理。
                                  </p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="permission-scope-row">
                          <label className="permission-scope-mode">
                            <strong>订单</strong>
                            <select
                              aria-label={`${app.name}订单范围`}
                              value={orderMode}
                              disabled={scopedDisabled}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value !== 'all')
                                  setEmptyOrderModes((current) => ({
                                    ...current,
                                    [app.id]: value === 'specific' ? 'specific' : 'none',
                                  }));
                                editGroup(app.id, (group) => ({
                                  ...group,
                                  orderIds: value === 'all' ? null : [],
                                  ...(value === 'none' && group.includeCentralOrders
                                    ? { includeCentralOrders: false }
                                    : {}),
                                }));
                              }}
                            >
                              <option value="all">所选城市与线路的全部订单</option>
                              <option value="specific">仅选定订单</option>
                              <option value="none">不管理订单</option>
                            </select>
                          </label>
                          <label className="permission-central-orders">
                            <input
                              type="checkbox"
                              aria-label={`${app.name}包含尚未分配部门的订单`}
                              checked={scope.includeCentralOrders === true}
                              disabled={
                                scopedDisabled ||
                                (!scope.includeCentralOrders &&
                                  (scope.cityIds === null ||
                                    scope.cityIds.length === 0 ||
                                    scope.productIds?.length === 0 ||
                                    orderMode === 'none'))
                              }
                              onChange={(event) => {
                                if (!event.target.checked) {
                                  editGroup(app.id, (group) => ({
                                    ...group,
                                    includeCentralOrders: false,
                                  }));
                                  return;
                                }
                                confirmDraft({
                                  title: '包含尚未分配部门的订单？',
                                  description: `仅增加“${app.name}”所选城市、线路和订单范围内的中央订单；新订单也按此范围校验。不会获得其他部门订单，也不会自动获得退款执行等操作。`,
                                  action: () => {
                                    const latest = latestDraft.current;
                                    const currentScope = latest.groups?.find(
                                      (group) => group.appId === app.id,
                                    );
                                    if (
                                      latest.disabled ||
                                      !currentScope ||
                                      JSON.stringify(currentScope) !== JSON.stringify(scope)
                                    ) {
                                      setFeedback('管理范围已发生变化，请重新确认中央订单授权。');
                                      setConfirmation(undefined);
                                      return;
                                    }
                                    onChange(
                                      latest.permissions,
                                      copyGroups(latest.groups ?? []).map((group) =>
                                        group.appId === app.id
                                          ? { ...group, includeCentralOrders: true }
                                          : group,
                                      ),
                                    );
                                    setConfirmation(undefined);
                                  },
                                });
                              }}
                            />
                            <span>
                              包含尚未分配部门的订单
                              <small>
                                部门订单按用户所属组织限制；此选项只补充尚未分配部门的中央订单。
                              </small>
                            </span>
                          </label>
                          {scope.cityIds === null && (
                            <p className="permission-scope-note">
                              需明确选择城市后才能增加中央订单，避免以后开放城市自动扩大范围。
                            </p>
                          )}
                          {orderMode === 'specific' && (
                            <>
                              <div className="permission-order-search">
                                <input
                                  type="search"
                                  className="permission-scope-search"
                                  aria-label={`${app.name}查找订单`}
                                  placeholder={
                                    api ? '输入订单号查找历史订单' : '输入订单号或线路名称查找'
                                  }
                                  value={orderSearch}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setScopeSearch((current) => ({
                                      ...current,
                                      [app.id]: { ...current[app.id], order: value },
                                    }));
                                  }}
                                />
                                {api && (
                                  <button
                                    type="button"
                                    aria-label={`${app.name}服务端查找订单`}
                                    disabled={!orderSearch.trim()}
                                    aria-busy={orderLookups[app.id]?.busy || undefined}
                                    onClick={() => void searchOrders(app.id, orderSearch)}
                                  >
                                    查找订单
                                  </button>
                                )}
                              </div>
                              {data.orderCandidatesLimit && (
                                <p className="permission-scope-note">
                                  默认候选最多显示最近 {data.orderCandidatesLimit}{' '}
                                  条订单，可输入订单号查找更早的订单。
                                </p>
                              )}
                              {orderLookups[app.id]?.busy && <p role="status">正在查找订单…</p>}
                              {orderLookups[app.id]?.error && (
                                <Notice tone="warning">{orderLookups[app.id]?.error}</Notice>
                              )}
                              {orderLookups[app.id]?.hasMore && (
                                <p className="permission-scope-note">
                                  匹配订单较多，请输入更完整的订单号缩小范围。
                                </p>
                              )}
                              <div className="permission-scope-choices permission-record-choices">
                                {displayedOrders.map((order) => (
                                  <label key={order.id}>
                                    <input
                                      type="checkbox"
                                      aria-label={`${app.name}订单：${order.orderNo}`}
                                      checked={scope.orderIds?.includes(order.id) ?? false}
                                      disabled={scopedDisabled}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        setEmptyOrderModes((current) => ({
                                          ...current,
                                          [app.id]: 'specific',
                                        }));
                                        editGroup(app.id, (group) => ({
                                          ...group,
                                          orderIds: toggleId(
                                            group.orderIds ?? [],
                                            order.id,
                                            checked,
                                          ),
                                        }));
                                      }}
                                    />
                                    <span>
                                      {order.orderNo}
                                      <small>
                                        {productNames.get(order.productId) ?? '原有线路'}
                                      </small>
                                    </span>
                                  </label>
                                ))}
                                {!orders.length && <p>当前城市与线路范围内暂无可选择订单。</p>}
                                {orders.length > 0 && displayedOrders.length === 0 && (
                                  <p>没有匹配的订单，请调整关键词。</p>
                                )}
                                {hiddenOrders.map((id) => (
                                  <label className="permission-retained-choice" key={id}>
                                    <input
                                      type="checkbox"
                                      checked
                                      disabled={scopedDisabled}
                                      aria-label={`保留订单：${orderNames.get(id) ?? '原有订单'}`}
                                      onChange={() => {
                                        setEmptyOrderModes((current) => ({
                                          ...current,
                                          [app.id]: 'specific',
                                        }));
                                        editGroup(app.id, (group) => ({
                                          ...group,
                                          orderIds:
                                            group.orderIds?.filter((item) => item !== id) ?? [],
                                        }));
                                      }}
                                    />
                                    {orderNames.get(id) ?? '原有订单'}（不在当前可选范围，配置保留）
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                          {scope.orderIds?.length === 0 && (
                            <p className="permission-scope-note">
                              {orderMode === 'specific'
                                ? '未选定订单，当前不管理订单。请勾选需要管理的订单。'
                                : '当前不管理订单。'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            {!data.apps.length && <p className="permission-empty">暂无可选择的小程序。</p>}
            {groups.some((group) => !appNames.has(group.appId)) && (
              <Notice>部分原有小程序未在当前目录中展示，其范围配置已保留。</Notice>
            )}
          </>
        )}
      </section>
      <ConfirmDialog
        open={Boolean(confirmation)}
        title={confirmation?.title ?? ''}
        description={confirmation?.description ?? ''}
        confirmDisabled={disabled}
        onCancel={() => setConfirmation(undefined)}
        onConfirm={() => {
          confirmation?.action();
          setConfirmation(undefined);
        }}
      />
    </div>
  );
}
